import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const ViewQuestionsPage = () => {
  const [backendUrl, setBackendUrl] = useState("");
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState({});
  const [statusMessage, setStatusMessage] = useState("");

  const { courseId, examId } = useParams();

  // Load backend URL from config.js
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/config.js";
    script.onload = () => {
      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      } else {
        console.error("❌ BACKEND_URL not found in config.js");
        setStatusMessage("⚠️ Configuration error. Backend URL not found.");
      }
    };
    script.onerror = () => {
      console.error("❌ Failed to load config.js");
      setStatusMessage("⚠️ Failed to load configuration file.");
    };
    document.body.appendChild(script);
  }, []);

  // Fetch questions
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!backendUrl || !courseId || !examId) return;
      try {
        setLoading(true);
        const response = await axios.get(
          `${backendUrl}/exams/view-questions/${courseId}/${examId}`
        );
        setQuestions(response.data || []);
      } catch (error) {
        console.error("❌ Error fetching questions:", error);
        setStatusMessage("⚠️ Failed to load questions.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [backendUrl, courseId, examId]);

  // Update question status (Active / Inactive)
  const handleStatusChange = async (questionId, newStatus) => {
    if (!backendUrl) return;
    try {
      setSelectedStatus((prev) => ({ ...prev, [questionId]: newStatus }));
      await axios.put(`${backendUrl}/exams/update-question-status`, {
        questionId,
        status: newStatus,
      });
      setStatusMessage("✅ Status updated successfully.");
      setQuestions((prev) =>
        prev.map((q) =>
          q.Question_ID === questionId ? { ...q, Status: newStatus } : q
        )
      );
    } catch (error) {
      console.error("❌ Error updating status:", error);
      setStatusMessage("⚠️ Failed to update status.");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h3>Loading questions...</h3>
      </div>
    );
  }

  return (
    <div style={{ padding: "30px", fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ color: "#E30016", marginBottom: "20px" }}>
        View Questions (Course #{courseId} | Exam #{examId})
      </h2>

      {statusMessage && (
        <div
          style={{
            backgroundColor: "#f8f8f8",
            padding: "10px",
            borderRadius: "6px",
            color: "#333",
            marginBottom: "15px",
            borderLeft: "5px solid #E30016",
          }}
        >
          {statusMessage}
        </div>
      )}

      {questions.length === 0 ? (
        <p>No questions found for this exam.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid #ddd",
            }}
          >
            <thead style={{ backgroundColor: "#E30016", color: "white" }}>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Question Text</th>
                <th style={thStyle}>Options</th>
                <th style={thStyle}>Correct Answer</th>
                <th style={thStyle}>Marks</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q, index) => (
                <tr key={q.Question_ID}>
                  <td style={tdStyle}>{index + 1}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    {q.Question_Text}
                  </td>
                  <td style={tdStyle}>
                    <ul style={{ paddingLeft: "18px", margin: 0 }}>
                      {q.Options?.split("||").map((opt, i) => (
                        <li key={i}>{opt.trim()}</li>
                      ))}
                    </ul>
                  </td>
                  <td style={tdStyle}>{q.Correct_Answer}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    {q.Marks}
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={selectedStatus[q.Question_ID] || q.Status}
                      onChange={(e) =>
                        handleStatusChange(q.Question_ID, e.target.value)
                      }
                      style={{
                        padding: "6px 10px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        fontWeight: "600",
                        backgroundColor:
                          (selectedStatus[q.Question_ID] || q.Status) ===
                          "Active"
                            ? "#e7f8e7"
                            : "#f9e3e3",
                      }}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Reusable table styles
const thStyle = {
  border: "1px solid #ddd",
  padding: "10px",
  textAlign: "left",
  fontWeight: "bold",
};

const tdStyle = {
  border: "1px solid #ddd",
  padding: "10px",
  verticalAlign: "top",
};

export default ViewQuestionsPage;
